import { motion } from 'framer-motion';
import { siteData } from '../../../config/siteData';
import { useState, useEffect } from 'react';

const Hero = () => {
  const [animationKey, setAnimationKey] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimationKey(prev => prev + 1);
          }
        });
      },
      { threshold: 0.3 }
    );

    const section = document.getElementById('home');
    if (section) {
      observer.observe(section);
    }

    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      const navHeight = 80;
      const elementPosition = contactSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const scrollToAbout = () => {
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      const navHeight = 80;
      const elementPosition = aboutSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - navHeight;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden pt-16">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://static.readdy.ai/image/102409a7b7d5861e272bf6d8b1888287/86d9836451138abe4288ffb64bee6368.png"
          alt="London Financial District"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-gray/50 to-navy/70"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 py-16 sm:py-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            key={`hero-left-${animationKey}`}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-5 sm:space-y-6"
          >
            {/* Branding */}
            <div className="space-y-2">
              <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-bold tracking-wide">
                <span className="text-gold">DULAIR</span>
                <span className="text-gold/70 mx-2">|</span>
                <span className="text-white text-base sm:text-lg md:text-xl font-medium">Private Family Office</span>
              </h2>
              <div className="inline-flex items-center space-x-2 px-3 py-1.5 border border-gold/40 rounded-full bg-black/30 backdrop-blur-sm">
                <span className="text-gold text-xs font-medium tracking-widest">EST. 1996 • LONDON</span>
              </div>
            </div>

            {/* Main Headline */}
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight">
              <span className="text-white">Preserving</span>
              <br />
              <span className="text-gold">Generational</span>
              <br />
              <span className="text-white">Wealth</span>
            </h1>

            {/* Description */}
            <p className="text-white/90 text-sm sm:text-base leading-relaxed max-w-lg">
              A distinguished private family office specializing in property investments, healthcare ventures, and strategic portfolio management for sophisticated investors.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row flex-wrap gap-3 pt-1">
              <button
                onClick={scrollToContact}
                className="px-6 py-3 bg-gold text-navy text-sm font-semibold rounded-full hover:bg-gold/90 hover:shadow-lg hover:shadow-gold/20 transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
              >
                Schedule Consultation
              </button>
              <button
                onClick={scrollToAbout}
                className="px-6 py-3 border-2 border-gold/80 text-gold text-sm font-semibold rounded-full hover:bg-gold hover:text-navy transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
              >
                Learn More <i className="ri-arrow-right-line ml-2"></i>
              </button>
              <button
                onClick={() => { const btn = document.querySelector('#vapi-widget-floating-button') as HTMLElement; if (btn) btn.click(); }}
                className="px-6 py-3 bg-white/15 backdrop-blur-sm border border-white/30 text-white text-sm font-semibold rounded-full hover:bg-white/25 hover:border-gold/60 hover:text-gold transition-all duration-300 whitespace-nowrap cursor-pointer text-center"
              >
                <i className="ri-message-3-line mr-2"></i>Chat with Us
              </button>
            </div>
          </motion.div>

          {/* Right Content */}
          <motion.div
            key={`hero-right-${animationKey}`}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden lg:block"
          >
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 lg:p-8 border border-white/20 shadow-2xl">
              <p className="text-white/90 text-sm leading-relaxed mb-6">
                With over a decade of excellence in wealth preservation and growth, Dulair Private Family Office provides bespoke investment solutions across property, healthcare, and strategic sectors. Our commitment to discretion, expertise, and long-term value creation has made us the trusted partner for discerning families and high-net-worth individuals.
              </p>
              <div className="grid grid-cols-3 gap-6 pt-6 border-t border-white/20">
                <div className="text-center">
                  <div className="text-gold text-2xl lg:text-3xl font-bold mb-1">{siteData.assetsUnderManagement}</div>
                  <div className="text-white/80 text-xs leading-tight">Assets Under<br />Management</div>
                </div>
                <div className="text-center">
                  <div className="text-gold text-2xl lg:text-3xl font-bold mb-1">{siteData.investmentProperties}</div>
                  <div className="text-white/80 text-xs leading-tight">Investment<br />Properties</div>
                </div>
                <div className="text-center">
                  <div className="text-gold text-2xl lg:text-3xl font-bold mb-1">{siteData.yearsExperience}</div>
                  <div className="text-white/80 text-xs leading-tight">Years<br />Experience</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        key={`hero-scroll-${animationKey}`}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1, repeat: Infinity, repeatType: 'reverse' }}
        className="absolute bottom-6 left-1/2 transform -translate-x-1/2 cursor-pointer"
        onClick={scrollToAbout}
      >
        <div className="w-5 h-8 border-2 border-gold/70 rounded-full flex items-start justify-center p-1.5">
          <div className="w-1 h-1.5 bg-gold rounded-full"></div>
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
